import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Login.css';
import '../styles/Checkbox.css';
import { useTranslation } from 'react-i18next';
import { useSession } from '../contexts/SessionContext';
import { ConsentConfig } from '../domain/Consent';
import { useAuthAPI } from '../services/auth_api/AuthAPIHook';
import { authAPI } from '../services/auth_api/SessionAuthAPI';
import UserInfo from '../components/UserInfo';
import { errorConsentNotFound } from '../services/auth_api/Errors';


const ConsentScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { sessionData } = useSession();
  const { getConsentConfig, submitConsent, sendAuthRequest } = useAuthAPI(authAPI);
  const [consentConfig, setConsentConfig] = useState<ConsentConfig | null>(null);
  const [selectedSites, setSelectedSites] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(true);



  useEffect(() => {
    const fetchConsentConfig = async () => {
      try {
        const res = await getConsentConfig();
        console.log('Consent config:', res);
        setConsentConfig(res);
      } catch (err) {
        if (err === errorConsentNotFound) {
          console.error("Consent not found");
          try {
            const res = await sendAuthRequest();
            window.location.replace(res.callback_url);
            return;
          }
          catch (err) {
            console.error("Failed to send auth request:", err);
            navigate('/login/error', { state: { errorMessage: t('error_unexpected'), error: 'internal_error', } });
          }
        }
        console.error("Failed to load consent:", err);
        navigate('/login/error', { state: { errorMessage: t('error_unexpected'), error: 'internal_error', } });
      } finally {
        setLoading(false);
      }
    };

    fetchConsentConfig();
  }, [navigate, t, getConsentConfig, sendAuthRequest]);

  useEffect(() => {
    if (!sessionData) {
      console.log("No session data found");
      navigate('/login/error', { state: { errorMessage: t('error_unexpected'), error: 'internal_error', } });
    }
  }, [sessionData, navigate, t]);

  if (!sessionData) {
    return null; // Return null while redirecting to avoid rendering the component
  }

  const handleSiteSelection = (resource_id: string) => {
    setSelectedSites((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(resource_id)) {
        newSelected.delete(resource_id);
      } else {
        newSelected.add(resource_id);
      }
      return newSelected;
    });
  };

  const handleSubmitConsent = async () => {
    if (selectedSites.size === 0) {
      return;
    }

    setLoading(true);

    const grants = new Map([['site', Array.from(selectedSites)]])

    const mandatoryResources = consentConfig?.available_resources.filter(
      res => res.resource_type !== 'site'
    )
    if (mandatoryResources) {
      for (const res of mandatoryResources) {
        if (grants.has(res.resource_type)) {
          grants.get(res.resource_type)?.push(res.resource_id)
        } else {
          grants.set(res.resource_type, [res.resource_id])
        }
      }
    }
    try {
      await submitConsent(grants);
      const res = await sendAuthRequest();
      window.location.replace(res.callback_url);
    } catch (err) {
      console.error("Failed to submit consent and send auth request:", err);
      navigate('/login/error', {
        state: {
          errorMessage: t('error_unexpected'),
          error: 'internal_error',
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/login/error', {
      state: {
        errorMessage: t('error_consent_rejected'),
        error: 'consent_rejected',
      }
    });
  };

  if (loading) {
    return (
      <div className="login-container">
        <div className="logo"></div> {/* Placeholder for the logo */}
        <h2 className="login-title">{t('ui_consent_request')}</h2>
        <UserInfo sessionData={sessionData} />
        <div className="login-box">
          <div className="mt">
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="logo"></div> {/* Placeholder for the logo */}
      <h2 className="login-title">{t('ui_consent_request')}</h2>
      <UserInfo sessionData={sessionData} />
      <div className="login-box">
        {/* User-friendly description */}
        <p className="text-justify" dangerouslySetInnerHTML={
          { __html: t('ui_select_site_consent_request_message', { client_name: consentConfig?.client_name }) }
        } />

        {/* Mapping through available resources */}
        {consentConfig?.available_resources?.map((resource) => (
          <div key={resource.resource_id} className="consent-resource">
            {resource.resource_type === 'site' && (
              <label className="site-checkbox site-checkbox-label">
                <input
                  type="checkbox"
                  onChange={() => handleSiteSelection(resource.resource_id)}
                  checked={selectedSites.has(resource.resource_id)}
                />
                <span className="custom-checkbox"></span>
                {resource.resource_id}
              </label>
            )}
          </div>
        ))}
        <button className="mt submit-button" onClick={handleSubmitConsent} disabled={selectedSites.size === 0}>
          {t('ui_submit_consent')}
        </button>
        <button className="back-button" onClick={handleCancel}>
          {t('ui_cancel')}
        </button>
      </div>
    </div>
  );
}

export default ConsentScreen;
